import { useState, useEffect } from 'react'
import loadable from '@loadable/component'
import { Helmet } from "react-helmet-async"
import { arrayMoveImmutable } from 'array-move'

import type {
    TPossibleScreens,
    TUpload,
    TUploadedFile,
    TUploadingError,
    TErrorText,
    TPage,
} from "../types"

import {
    saveThumb,
    loadThumb,
} from '../helpers'

import { baseURL, development, isSnap } from '../constants'

import { statOnLoad } from '../Stat'

import Wrapper from "../elements/Wrapper"
import DefaultContent, { Content } from "../elements/Content"

import {
    Buttons,
    UploadButtons,
    CloudButtons,
    ButtonOneDrive,
    ButtonDropBox,
    ButtonGoogleDrive,

    PagePreviwButtons,
    PagePreviwButton,
} from "../elements/Buttons"

import { convert, localUpload } from "../Services"

import DropZone from "../screens/DropZone"

const ErrorBlock = loadable(() => import('../elements/Errors'))
const PagesPreview = loadable(() => import('../screens/PagesPreview'))
const Preloader = loadable(() => import("../screens/Preloader"))
const Download = loadable(() => import("../screens/Download"))
const Error = loadable(() => import("../screens/Error"))

const uploadedFilesT: TUploadedFile[] = []
const uploadingErrors: TUploadingError[] = []
let downloadLink: string


const SplitPDF = () => {
    const [currentScreen, switchScreen] = useState<TPossibleScreens>('filespreview')
    const [progress, setProgress] = useState(0)
    const [uploadedFiles, setUploadedFiles] = useState<TUploadedFile[]>(uploadedFilesT)
    const [countUploadingErrors, setCountUploadingErrors] = useState(uploadingErrors.length)
    const [errorText, setErrorText] = useState<TErrorText>({})
    const [showErrorBlock, setShowErrorBlock] = useState(true)
    const [pages, setPages] = useState<TPage[]>([])
    const [disableButton, setDisableButton] = useState(true)

    const [prep, setPrep] = useState('Uploading File')

    const upload: TUpload = {

        action: "split_pdf",
        allowExtantions: ['pdf'],
        convertTo: "pdf",
        maxSize: 25, // MB
        multiselect: false,

        setDownloadLink: (link: string) => downloadLink = link,
        getDownloadLink: () => downloadLink,
        setProgress: (progress) => setProgress(progress),
        getProgress: () => progress,
        setScreen: (screen) => {
            if (window.scrollY > 220) window.scrollTo({ top: 0, behavior: "smooth" })
            if (screen === 'uploading' || screen === 'converting' || screen === 'preparing') setProgress(0)
            return switchScreen(screen)
        },
        getScreen: () => currentScreen,
        addFiles: (uploadedFile) => {
            return setUploadedFiles([...uploadedFile])
        },
        delFile: (uploadedFile) => {
            return setUploadedFiles(uploadedFiles.filter((element) => element !== uploadedFile))
        },
        clearFiles: () => setUploadedFiles([]),
        getFiles: () => uploadedFiles,
        moveFiles: (oldIndex, newIndex) => {
            return setUploadedFiles(arrayMoveImmutable(uploadedFiles, oldIndex, newIndex))
        },
        addError: (uploadingError) => {
            uploadingErrors.push(uploadingError)
            setShowErrorBlock(true)
            return setCountUploadingErrors(countUploadingErrors + 1)
        },
        delError: (uploadingError) => {
            const idx = uploadingErrors.indexOf(uploadingError)
            if (idx !== -1) {
                uploadingErrors.splice(idx, 1)
                return setCountUploadingErrors(countUploadingErrors - 1)
            }
        },
        clearErrors: () => {
            uploadingErrors.splice(0, uploadingErrors.length)
            return setCountUploadingErrors(0)
        },
        hideErrors: () => setShowErrorBlock(false),
        getErrors: () => uploadingErrors,
        setErrorText: setErrorText,

        getPages: () => pages,
        setPages: (newPages) => {
            if (newPages[newPages.length - 1].file_flag == 0) setDisableButton(true)
            else setDisableButton(false)

            return setPages(newPages)
        },
        setDefaultPages: saveThumb,
    }

    useEffect(() => {
        
        const token = location.hash.replace('#', '')

        if (token.includes("id=")) {
            const docid = token.slice(3);
            if (Number.isInteger(Number(docid)) && String(Number(docid)) == docid) {
                localUpload(upload, docid, true)
                setPrep('Prepairing File')
            }
        }
        else if (token.length === 36) {
            localUpload(upload, token)
            setPrep('Prepairing File')
        }
        else statOnLoad(upload)
    },[])


    // useEffect(() => {
    //     const token = location.hash.replace('#', '')
    //     if (token.length === 36) {
    //         location.hash = ''
    //         localUpload(upload, token)
    //     }
    //     else statOnLoad(upload)
    // },[])

    return (
        <Wrapper>
            <Helmet>
                <title>Split PDF | Templateroller</title>
                <meta name="keywords" content="split pdf, pdf split, split pdf pages, split pdf online, how to split pdf pages, how to split pdf pages, how to split a pdf, how to split up a pdf, how to split a pdf into multiple files, how to split pdf in adobe, separate pdf pages, how to separate pages in pdf, separate pdf, separate pdf pages free" />
                <meta name="description" content="Split PDF pages online for free! Use TemplateRoller's online tool to separate PDF pages and save the result to your device in seconds." />
                <meta property='og:title' content="Split PDF" />
                <meta property='og:description' content="Split PDF files into single pages with TemplateRoller’s free online service. You can separate PDF pages of any document regardless of its length" />
                <meta property='og:url' content={`${baseURL}/tools/split-pdf.html`} />
                <meta property="og:image" content={`${baseURL}/tools/assets/og-split-pdf.png`} />
                <meta property='og:locale' content="en_US" />
                <meta property='twitter:title' content="Split PDF" />
                <meta property='twitter:description' content="Split PDF files into single pages with TemplateRoller’s free online service. You can separate PDF pages of any document regardless of its length" />
                <meta property="twitter:image:src" content={`${baseURL}/tools/assets/og-split-pdf.png`} />
                <link rel='canonical' href={`${baseURL}/tools/split-pdf.html`} />
            </Helmet>

            {
                currentScreen === 'filespreview' &&
                <h1>Split&nbsp;PDF</h1>
            }

            {
                currentScreen === 'filespreview' &&
                (uploadedFiles.length === 0 || pages.length === 0) &&
                <DefaultContent>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <DropZone
                        title="Split PDF online with ease: separate PDF document quickly and efficiently"
                        description="Split PDF files into single pages with TemplateRoller’s free online service. You can separate PDF pages of any document regardless of its length"
                        buttonText="Browse file"
                        warning={`Upload document of up to ${upload.maxSize} MB in ${(upload.allowExtantions.length > 2 ? upload.allowExtantions.map((e, i) => i == upload.allowExtantions.length - 1 ? `and .${e}` : `.${e}`) : upload.allowExtantions.map(e => `.${e}`)).join(', ')} format${upload.allowExtantions.length > 1 ? 's' : ''}`}

                        upload={upload}
                    />
                    <Buttons>
                        <UploadButtons>
                            <CloudButtons>
                                <ButtonDropBox upload={upload} />
                                <ButtonOneDrive upload={upload} />
                                <ButtonGoogleDrive upload={upload} />
                            </CloudButtons>
                        </UploadButtons>
                    </Buttons>

                </DefaultContent>

            }

            {
                currentScreen === 'filespreview' &&
                uploadedFiles.length > 0 &&
                pages.length > 0 &&
                <Content>
                    {
                        countUploadingErrors > 0 &&
                        showErrorBlock &&
                        <ErrorBlock upload={upload} />
                    }

                    <PagesPreview upload={upload} sortable={false} />

                    <Buttons>
                        <PagePreviwButtons>
                            <PagePreviwButton
                                primary={false}
                                onClick={() => {
                                    if (disableButton) {
                                        if (confirm('Do you want to upload again?')) {
                                            setUploadedFiles([])
                                            return setPages([])
                                        }
                                    }
                                    else {
                                        const thumb = loadThumb()
                                        if (confirm('Do you want to discard changes?') && thumb) {
                                            setDisableButton(true)
                                            return setPages(thumb)
                                        }
                                    }
                                }}
                            >{`${disableButton ? 'Reset' : 'Undo'}`}</PagePreviwButton>
                            <PagePreviwButton
                                primary={true}
                                disabled={disableButton}
                                onClick={() => {
                                    return convert({
                                        upload: upload,
                                    })
                                }}
                            >Apply</PagePreviwButton>
                        </PagePreviwButtons>
                    </Buttons>
                </Content>
            }

            {
                currentScreen === 'uploading' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Uploading File"
                        description="Don’t close this tab, your files are being uploaded. This shouldn’t take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'preparing' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Preparing Pages"
                        description="Don’t close this tab, your files are being prepared. This won't take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'converting' &&
                <DefaultContent>
                    <Preloader
                        type="progress"
                        title="Processing Document"
                        description="Don’t close this tab, your files are being processed. This won't take long."
                        progress={progress}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'link' &&
                <DefaultContent>
                    <Download
                        title="Split PDF"
                        description="Your file has been splitted. Click on “Download” to save the file to your device as a ZIP archive."
                        buttonText="Download"

                        upload={upload}
                        onClick={() => {
                            // @ts-ignore
                            window.stat?.(`tools_result_${upload.action}`)
                        }}
                    />
                </DefaultContent>
            }

            {
                currentScreen === 'error' &&
                <DefaultContent>
                    <Error text={errorText} />
                </DefaultContent>
            }

        </Wrapper>
    )
}

export default SplitPDF